import { forwardRef, useMemo } from 'react';

import { Box, Divider, Flex, useTheme } from '@chakra-ui/react';
import { InitiativeTaskSectionHeader } from 'components/RabbitInitiativeSidePanel/Components/SectionHeaders';
import { InitiativeTaskRow } from 'components/RabbitInitiativeSidePanel/PrimaryPanel/Sections/Task/InitiativeTaskRow';
import { useOKRInitiativesData } from 'components/RabbitOKRSidePanel/datahooks/useOKRInitiativesData';
import { openCreateInitiativePanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { useEmployeeGoalsPrivileges } from 'components/hooks/useEmployeeGoalsPrivileges';
import { useDispatch, useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { PlusNotOutlinedWhite } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/noInitiatives.svg';

import { sendOpenedInitiativePanel } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextHeader2, TextHeader3 } from 'uiToolkitV1/Typography';

const OKRInitiatives = ({
  isOKRPanelDisabled,
  isUpdating,
  buttonText = 'Add Initiative',
  headerText = 'Child Initiatives',
  isOpen,
  ids,
}) => {
  const dispatch = useDispatch();

  const { data = [], isLoading } = useOKRInitiativesData({
    enabled: isOpen,
    id: ids,
  });

  const { canCreateInitiativePrivilege } = useEmployeeGoalsPrivileges();

  const { toIds = [], startTS, dueTS } = useSelector(kRDrawerSelector);

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const updatedToIds = useMemo(
    () =>
      toIds.map((data) => ({
        uuid: data?.id,
        displayName: data.displayName || data.metadata.displayName,
        email: data.email || data.metadata?.email,
        profilePhoto: data.profilePhoto || data?.profile?.profilePhoto,
        ownerType: data?.ownerType,
      })),
    [toIds]
  );
  const theme = useTheme();
  const page = useViewPageName();

  const onAddNewClick = () => {
    sendOpenedInitiativePanel({ eventProperties: { page, source: 'Edit KR Panel' } });

    dispatch(
      openCreateInitiativePanel({
        parentPost: {
          goal: [
            {
              uuid: ids[0],
            },
          ],
        },
        parentPosts: {
          goal: [
            {
              uuid: ids[0],
            },
          ],
        },

        toIds: updatedToIds,
        startTS,
        dueTS,
      })
    );
  };

  const HeaderComponent = () => (
    <Box my={1} pt={2} textAlign="center">
      <TextHeader2 fontSize="18px" bold color={theme.colors.text.g1}>
        You haven't created an Initiative
      </TextHeader2>
      <TextHeader3 regular color={theme.colors.text.g2}>
        The best way to have a garden full of roses is to plant them! Take initiative and get started today!
      </TextHeader3>
    </Box>
  );

  return (
    <Flex flexDirection="column" alignItems="stretch" gap="12px" flexWrap="nowrap" w="100%">
      <InitiativeTaskSectionHeader
        buttonText={buttonText}
        headerText={headerText}
        onClick={onAddNewClick}
        isCreationAllowed={canCreateInitiativePrivilege && data.length !== 0 && !isOKRPanelDisabled}
      />
      <Divider sx={{ borderColor: '#CFD7DF' }} />

      <Flex flexDir="column">
        {isLoading ? (
          <SkeletonLoader />
        ) : data.length === 0 ? (
          <EmptyScreen
            illustrationStyle={{ width: '80px', margin: 'auto' }}
            headerComp={HeaderComponent}
            illustrationSrc={NoDataImg}
            CTAComp={() => {
              if (canCreateInitiativePrivilege && !isOKRPanelDisabled)
                return (
                  <Button onClick={onAddNewClick} StartIcon={PlusNotOutlinedWhite}>
                    Add New Initiative
                  </Button>
                );
            }}
          />
        ) : null}

        {data.length > 0 &&
          data.map((data) => {
            const { id, text, detailedStatus, subTasksCount, dueTS, toIds, status, visibilityType } = data;

            return (
              <InitiativeTaskRow
                goalName={text}
                taskStatus={detailedStatus}
                subTasksCount={subTasksCount}
                status={status}
                dueDate={dueTS}
                employees={toIds}
                showDivider
                data={data}
                id={id}
                key={id}
                panelType="GOAL"
                parentId={ids?.[0]}
                isOKRPanel
                visibilityType={visibilityType}
                employeeId={loggedInEmployeeId}
                isOKRPanelDisabled={isOKRPanelDisabled}
              />
            );
          })}
      </Flex>
    </Flex>
  );
};

export default OKRInitiatives;

export const RefableOKRInitiatives = forwardRef((props, ref) => (
  <Box ref={ref} w="100%">
    <OKRInitiatives {...props} />
  </Box>
));
